/**
 * @fileoverview ContactModal Component - Contact Form with CAPTCHA
 *
 * This component provides a modal contact form for users to request
 * custom solutions. Includes a canvas-based CAPTCHA for spam prevention.
 *
 * @description
 * Key features:
 * - Modal overlay with form fields
 * - Canvas-based CAPTCHA generation and validation
 * - Form submission to backend API
 * - Success confirmation state
 * - Use case dropdown for request categorization
 *
 * @requires React
 * @requires ../utils/constants - API URL configuration
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { API_URL } from '../utils/constants';

/**
 * ContactModal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @returns {JSX.Element|null}
 */
function ContactModal({ isOpen, onClose }) {
  /* ========================================================================
     State Management
     ======================================================================== */

  /**
   * Form Data State
   * Contains all form field values
   */
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    useCase: 'bulk_processing'
  });

  /**
   * Form Status State
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /**
   * CAPTCHA State
   * Canvas ref for drawing, generated text, and user answer
   */
  const captchaCanvasRef = useRef(null);
  const [captchaText, setCaptchaText] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  /* ========================================================================
     CAPTCHA Generation Functions
     ======================================================================== */

  /**
   * Generate random CAPTCHA text
   * Excludes ambiguous characters (0, O, 1, l, I)
   * @returns {string} 6-character random string
   */
  const generateCaptchaText = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let text = '';
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return text;
  };

  /**
   * Draw CAPTCHA text on canvas with distortion effects
   * Applies noise lines, rotated characters, and dots for security
   * @param {string} text - The text to render
   */
  const drawCaptcha = (text) => {
    const canvas = captchaCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 60;

    // Background with gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1e293b');
    gradient.addColorStop(1, '#334155');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add noise lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Draw text with variations
    ctx.font = 'bold 30px Arial';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const x = 25 + i * 28;
      const y = 30 + Math.random() * 10 - 5;
      const rotate = (Math.random() - 0.5) * 0.4;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotate);
      ctx.fillStyle = `hsl(${Math.random() * 360}, 70%, 70%)`;
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }

    // Add noise dots
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
      ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
    }
  };

  /**
   * Generate and render a new CAPTCHA
   * Resets the user's answer input
   */
  const refreshCaptcha = useCallback(() => {
    const newText = generateCaptchaText();
    setCaptchaText(newText);
    setCaptchaAnswer('');
    setTimeout(() => drawCaptcha(newText), 0);
  }, []);

  /* ========================================================================
     Effects
     ======================================================================== */

  /**
   * Effect: Generate CAPTCHA when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      refreshCaptcha();
    }
  }, [isOpen, refreshCaptcha]);

  /* ========================================================================
     Form Submission Handler
     ======================================================================== */

  /**
   * Handle form submission
   * Validates CAPTCHA and sends data to backend API
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verify CAPTCHA (case-insensitive)
    if (captchaAnswer.toLowerCase() !== captchaText.toLowerCase()) {
      alert('Incorrect CAPTCHA. Please try again.');
      refreshCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        console.log('Contact form submitted successfully:', result);
        setSubmitted(true);
      } else {
        console.error('Failed to submit contact form:', result);
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ========================================================================
     Render
     ======================================================================== */

  // Don't render if modal is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-center justify-center p-4 sm:p-6">
        <div className="bg-gradient-to-br from-gray-900 to-slate-900 border border-white/20 rounded-2xl max-w-2xl w-full p-4 sm:p-6 md:p-8 shadow-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Contact Us</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ================================================================
            Form Content
            - Success state (after submission)
            - Form fields (before submission)
            ================================================================ */}
        {submitted ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
            <p className="text-gray-400 mb-4">We'll get back to you soon.</p>
            <button
              onClick={() => {
                onClose();
                setSubmitted(false);
                setFormData({ name: '', email: '', company: '', message: '', useCase: 'bulk_processing' });
              }}
              className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          /* Contact Form */
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name and Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            {/* Company Field (Optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">Company (Optional)</label>
              <input
                type="text"
                value={formData.company}
                onChange={e => setFormData({...formData, company: e.target.value})}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors"
                placeholder="Acme Inc."
              />
            </div>

            {/* Use Case Dropdown */}
            <div>
              <label className="block text-sm font-medium mb-2">Use Case *</label>
              <select
                required
                value={formData.useCase}
                onChange={e => setFormData({...formData, useCase: e.target.value})}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors [&>option]:bg-gray-800 [&>option]:text-white"
              >
                <option value="bulk_processing">Bulk Image/Video Processing</option>
                <option value="larger_files">Larger File Size Support</option>
                <option value="api_integration">API Integration</option>
                <option value="enterprise">Enterprise Solution</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Message Textarea */}
            <div>
              <label className="block text-sm font-medium mb-2">Message *</label>
              <textarea
                required
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                rows="4"
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                placeholder="Tell us about your requirements..."
              />
            </div>

            {/* CAPTCHA Section
                - Canvas-rendered distorted text
                - Refresh button
                - User input field */}
            <div>
              <label className="block text-sm font-medium mb-2">Verification (Anti-Spam) *</label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="relative">
                  <canvas
                    ref={captchaCanvasRef}
                    className="border border-white/20 rounded-lg"
                    style={{ imageRendering: 'pixelated' }}
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
                    title="Refresh CAPTCHA"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
                <input
                  type="text"
                  required
                  value={captchaAnswer}
                  onChange={e => setCaptchaAnswer(e.target.value)}
                  className="flex-1 sm:flex-none w-full sm:w-40 bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 focus:outline-none focus:border-emerald-500 transition-colors"
                  placeholder="Enter text above"
                  autoComplete="off"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Type the characters you see (not case-sensitive)</p>
            </div>

            {/* Form Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}

export default ContactModal;
